import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import { writeEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/writeEditorContentFragment.ts';
import type { EditorContentFragmentWriteArgs } from '@confluence/editor-content-unified-query/entry-points/writeEditorContentFragment.ts';

import type { useTitleContentPropertiesForEditorQuery$data } from './__generated__/useTitleContentPropertiesForEditorQuery.graphql.ts';
import type { useTitleContentPropertiesFragment_content$key } from './__generated__/useTitleContentPropertiesFragment_content.graphql.ts';

// Copy of useTitleContentPropertiesForPublishedPage_content for EditorContentUnifiedQuery: View
// Page spreads the shared fragment @relay(mask: false), which rules out @catch on it. Kept equal by
// a parity test; field-identical to useTitleContentPropertiesForEditorQuery.
const titleContentPropertiesFragmentNode: GraphQLTaggedNode = graphql`
	fragment useTitleContentPropertiesFragment_content on Content @catch {
		id
		pageTitlePublished: properties(key: "page-title-property-published") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		pageTitleDraft: properties(key: "page-title-property-draft") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		metadata {
			frontend {
				coverPictureWidth
			}
		}
	}
`;

/**
 * The title content properties from EditorContentUnifiedQuery in the shape of
 * useTitleContentPropertiesForEditorQuery data. When the fragment is provided, its reader skips
 * useTitleContentPropertiesForEditorQuery, whose refetch still updates the records the fragment
 * reads.
 */
export function useTitleContentPropertiesFragment({
	contentId,
}: {
	contentId: string;
}): EditorContentFragmentResult<useTitleContentPropertiesForEditorQuery$data> {
	return useEditorContentFragment<
		useTitleContentPropertiesFragment_content$key,
		useTitleContentPropertiesForEditorQuery$data
	>('titleContentPropertiesFragment', titleContentPropertiesFragmentNode, {
		contentId,
		mapData: (value) => ({ content: { nodes: [value] } }),
	});
}

/**
 * Writes the draft title property the fragment reads. The node id is the page id, as the legacy
 * update writes it, so a page without the property yet gets one.
 */
export function writeTitleContentPropertiesFragment(
	args: EditorContentFragmentWriteArgs,
	{ key, value, versionNumber }: { key: string; value: string; versionNumber: number },
): void {
	writeEditorContentFragment<useTitleContentPropertiesFragment_content$key>(
		titleContentPropertiesFragmentNode,
		args,
		{
			pageTitleDraft: {
				nodes: [{ id: args.contentId, key, value, version: { number: versionNumber } }],
			},
		},
	);
}
